import { useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type {
  ReportExportColumn,
  ReportExportRequest,
  StockAlertExpiryRow,
  StockAlertLowRow,
  StockAlertsReport,
} from '@sellokart/shared';
import { PageHeader } from '@/components/PageHeader';
import { AsyncSection } from '@/components/AsyncSection';
import { ReportFilterBar } from '@/components/ReportFilterBar';
import { KpiCard } from '@/components/KpiCard';
import { SectionCard } from '@/components/SectionCard';
import { Badge } from '@/components/Badge';
import { DataTable, TableSkeleton } from '@/components/Table';
import type { Column } from '@/components/Table';
import { ExportMenu } from '@/components/ExportMenu';
import { downloadReportExport } from '@/features/reports/api';
import type { ExportFormat } from '@/features/reports/api';
import { DEFAULT_EXPIRY_DAYS, fetchStockAlerts, stockAlertsKeys } from '@/features/reports/stock-alerts-api';
import { useReportFilters } from '@/features/reports/useReportFilters';
import { usePortal } from '@/features/portal/PortalContext';
import { formatPosDate } from '@/lib/format';

const DAY_OPTIONS = [7, 14, 30, 60, 90] as const;

/** Quantities are decimal (weighed goods); trim trailing zeros like the till's `Formatters.qty`. */
function qty(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(3).replace(/\.?0+$/, '');
}

function daysLabel(days: number): string {
  if (days === 0) return 'Today';
  if (days < 0) return days === -1 ? '1 day ago' : `${-days} days ago`;
  return days === 1 ? '1 day' : `${days} days`;
}

function isEmptyReport(r: StockAlertsReport): boolean {
  return r.lowStock.length === 0 && r.expiringSoon.length === 0 && r.expired.length === 0;
}

const branchColumn = <T extends { branchName: string }>(): Column<T> => ({
  key: 'branch',
  header: 'Branch',
  cell: (row) => row.branchName || '—',
});

const LOW_COLUMNS: Column<StockAlertLowRow>[] = [
  { key: 'product', header: 'Product', cell: (r) => r.productName },
  {
    key: 'barcode',
    header: 'Barcode',
    visibility: 'hidden md:table-cell',
    cell: (r) => r.barcode || '—',
  },
  {
    key: 'quantity',
    header: 'In stock',
    align: 'right',
    cell: (r) => (
      <span className="inline-flex items-center gap-1.5">
        {qty(r.quantity)} {r.unit}
        {r.quantity <= 0 ? (
          <Badge tone="danger" dot>
            Out
          </Badge>
        ) : null}
      </span>
    ),
  },
  {
    key: 'reorder',
    header: 'Reorder level',
    align: 'right',
    cell: (r) => qty(r.reorderLevel),
  },
  {
    key: 'expiry',
    header: 'Earliest expiry',
    visibility: 'hidden md:table-cell',
    cell: (r) => r.earliestExpiry ?? '—',
  },
];

function expiryColumns(expired: boolean): Column<StockAlertExpiryRow>[] {
  return [
    { key: 'product', header: 'Product', cell: (r) => r.productName },
    { key: 'batch', header: 'Batch', cell: (r) => r.batchNo || '—' },
    { key: 'expiry', header: 'Expiry', cell: (r) => r.expiryDate },
    {
      key: 'days',
      header: expired ? 'Expired' : 'Expires in',
      align: 'right',
      cell: (r) => (
        <Badge tone={expired ? 'danger' : r.daysLeft <= 7 ? 'warning' : 'neutral'}>{daysLabel(r.daysLeft)}</Badge>
      ),
    },
    {
      key: 'lot',
      header: 'Lot qty',
      align: 'right',
      cell: (r) => `${qty(r.batchQuantity)} ${r.unit}`,
    },
    {
      key: 'total',
      header: 'Product stock',
      align: 'right',
      visibility: 'hidden md:table-cell',
      cell: (r) => qty(r.productQuantity),
    },
  ];
}

function withBranch<T extends { branchName: string }>(columns: Column<T>[], show: boolean): Column<T>[] {
  return show ? [columns[0], branchColumn<T>(), ...columns.slice(1)] : columns;
}

/**
 * All three sections in one sheet, in the page's order, told apart by a
 * leading Alert column. A low-stock row has no single lot, so its Batch and
 * days are blank and Expiry is the product's earliest; a lot row has no
 * reorder level of its own.
 */
function buildExport(report: StockAlertsReport, showBranch: boolean, branchName: string): ReportExportRequest {
  const columns: ReportExportColumn[] = [
    { header: 'Alert' },
    { header: 'Product', width: 2 },
    ...(showBranch ? [{ header: 'Branch' }] : []),
    { header: 'Barcode' },
    { header: 'Batch' },
    { header: 'Expiry' },
    { header: 'Days' },
    { header: 'Qty', align: 'right', numeric: true },
    { header: 'Unit' },
    { header: 'Product stock', align: 'right', numeric: true },
    { header: 'Reorder level', align: 'right', numeric: true },
  ];
  const branch = (name: string) => (showBranch ? [name || '—'] : []);
  const lot = (alert: string, r: StockAlertExpiryRow) => [
    alert,
    r.productName,
    ...branch(r.branchName),
    r.barcode,
    r.batchNo,
    r.expiryDate,
    daysLabel(r.daysLeft),
    qty(r.batchQuantity),
    r.unit,
    qty(r.productQuantity),
    '',
  ];
  return {
    title: 'Stock alerts',
    subtitle: `As of ${formatPosDate(report.asOf)} · ${branchName} · expiring within ${report.expiringWithinDays} days`,
    from: '',
    to: '',
    columns,
    rows: [
      ...report.expired.map((r) => lot('Expired', r)),
      ...report.expiringSoon.map((r) => lot('Expiring soon', r)),
      ...report.lowStock.map((r) => [
        r.quantity <= 0 ? 'Out of stock' : 'Low stock',
        r.productName,
        ...branch(r.branchName),
        r.barcode,
        '',
        r.earliestExpiry ?? '',
        '',
        qty(r.quantity),
        r.unit,
        qty(r.quantity),
        qty(r.reorderLevel),
      ]),
    ],
  };
}

export function StockAlertsPage() {
  const { profile } = usePortal();
  const [filters, setFilters] = useReportFilters();
  const [days, setDays] = useState<number>(DEFAULT_EXPIRY_DAYS);
  const showBranch = filters.branchId === '' && (profile?.branches.length ?? 0) > 1;
  const branchName = filters.branchId
    ? (profile?.branches.find((b) => b.id === filters.branchId)?.name ?? 'Selected branch')
    : 'All branches';

  const query = useQuery({
    queryKey: stockAlertsKeys.report(filters.branchId, days),
    queryFn: ({ signal }) => fetchStockAlerts(filters.branchId, days, signal),
  });

  return (
    <div>
      <PageHeader
        title="Stock alerts"
        description="Low stock, lots expiring soon, and expired lots still on the shelf — as the till's inventory banner shows them."
      />
      <ReportFilterBar filters={filters} onChange={setFilters} branches={profile?.branches ?? []} hideDateRange />
      <div className="mb-6 flex items-end gap-3">
        <div>
          <label className="label" htmlFor="stock-alerts-days">
            Expiring within
          </label>
          <select
            id="stock-alerts-days"
            className="input pr-9"
            value={days}
            onChange={(event) => setDays(Number(event.target.value))}
          >
            {DAY_OPTIONS.map((d) => (
              <option key={d} value={d}>
                {d} days
              </option>
            ))}
          </select>
        </div>
        {query.data && !isEmptyReport(query.data) ? (
          <div className="ml-auto">
            <ExportMenu
              onExport={(format: ExportFormat) =>
                downloadReportExport(
                  buildExport(query.data, showBranch, branchName),
                  format,
                )
              }
            />
          </div>
        ) : null}
      </div>
      <AsyncSection
        state={query}
        isEmpty={isEmptyReport}
        emptyTitle="No stock alerts"
        emptyDescription="Nothing is at or below its reorder level, and no lot with stock is expired or expiring in this window."
        skeleton={<TableSkeleton columns={6} />}
      >
        {(report) => (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <KpiCard label="Low stock" value={String(report.lowStock.length)} />
              <KpiCard
                label={`Expiring in ${report.expiringWithinDays} days`}
                value={String(report.expiringSoon.length)}
              />
              <KpiCard label="Expired, still in stock" value={String(report.expired.length)} />
            </div>

            <AlertSection
              id="stock-alerts-expired"
              title="Expired"
              description={`Lots past their expiry date (as of ${report.asOf}) that still hold stock.`}
              count={report.expired.length}
            >
              <DataTable
                caption="Expired lots"
                columns={withBranch(expiryColumns(true), showBranch)}
                rows={report.expired}
                rowKey={(r) => `${r.deviceId}:${r.batchSourceId}`}
                rowClassName={() => 'bg-rose-50'}
              />
            </AlertSection>

            <AlertSection
              id="stock-alerts-expiring"
              title="Expiring soon"
              description={`Lots with stock expiring between today and ${report.expiringWithinDays} days from now.`}
              count={report.expiringSoon.length}
            >
              <DataTable
                caption="Lots expiring soon"
                columns={withBranch(expiryColumns(false), showBranch)}
                rows={report.expiringSoon}
                rowKey={(r) => `${r.deviceId}:${r.batchSourceId}`}
              />
            </AlertSection>

            <AlertSection
              id="stock-alerts-low"
              title="Low stock"
              description="Products at or below their reorder level, lowest first."
              count={report.lowStock.length}
            >
              <DataTable
                caption="Low stock products"
                columns={withBranch(LOW_COLUMNS, showBranch)}
                rows={report.lowStock}
                rowKey={(r) => `${r.deviceId}:${r.sourceId}`}
                rowClassName={(r) => (r.quantity <= 0 ? 'bg-amber-50' : undefined)}
              />
            </AlertSection>
          </div>
        )}
      </AsyncSection>
    </div>
  );
}

function AlertSection({
  id,
  title,
  description,
  count,
  children,
}: {
  id: string;
  title: string;
  description: string;
  count: number;
  children: ReactNode;
}) {
  return (
    <SectionCard headingId={id} title={title} count={count} description={description}>
      {count === 0 ? <p className="px-4 py-6 text-sm text-slate-500">Nothing to flag.</p> : children}
    </SectionCard>
  );
}
